/**
 * Bundle module - cart-side grouping behaviour and line tagging.
 *
 * Two jobs, both small:
 *
 * 1. `theme.bundleProperties()` builds the line item properties every bundle
 *    configurator stamps onto its lines. Before this existed each line carried
 *    only `_bundle: '1'` - identical across every bundle in the cart - so two
 *    bundles were indistinguishable and a partially removed one was impossible
 *    to detect. The group id fixes that; `_bundle_count` is what lets the cart
 *    notice a line has gone missing.
 *
 * 2. Removing any line of a bundle asks first, then removes the whole group.
 *    Customers were removing one piece, losing the (admin-side) bundle discount
 *    with no explanation, and then raising a ticket about the missing item and
 *    the missing discount. The confirm makes both consequences explicit before
 *    anything changes.
 *
 * Nothing here is enforcement: a customer can still hit /cart/change directly.
 * It is a UI guard over an ordinary Shopify cart.
 */
(function () {
  'use strict';

  var LINE = '.cart-item[data-bundle-id]';

  function strings() {
    return (window.theme && theme.strings && theme.strings.bundle) || {};
  }

  /**
   * Line item properties for one bundle add-to-cart.
   *
   * `_bundle` is kept as-is: downstream reporting and the older orders already
   * key off it, and dropping it would split the two.
   *
   * `Bundle` has no underscore, so Shopify treats it as customer-visible and it
   * follows the line into checkout, the order confirmation email and the packing
   * slip. That alone answers most "why is there a pin in my order" tickets.
   *
   * @param {string} name  Bundle name, e.g. "Pathfinder Uniform Bundle".
   * @param {number} count How many lines this bundle is adding.
   * @returns {object} Properties to apply to every line of the bundle.
   */
  function bundleProperties(name, count) {
    var label = name || 'Bundle';

    return {
      _bundle: '1',
      _bundle_id: bundleId(),
      _bundle_name: label,
      _bundle_count: String(count),
      Bundle: label
    };
  }

  /**
   * Unique per add-to-cart click, so adding the same bundle twice produces two
   * independent groups rather than one group of double length.
   */
  function bundleId() {
    var random = Math.random().toString(36).slice(2, 10);
    return 'b' + Date.now().toString(36) + random;
  }

  /** Every line key belonging to a group, de-duplicated. */
  function lineKeys(bundleId) {
    var seen = Object.create(null);
    var keys = [];

    Array.prototype.forEach.call(document.querySelectorAll(LINE), function (el) {
      if (el.dataset.bundleId !== bundleId) return;

      // The cart page and the cart drawer can both be in the DOM at once, each
      // holding its own copy of the same line.
      var key = el.dataset.lineKey;
      if (!key || seen[key]) return;

      seen[key] = true;
      keys.push(key);
    });

    return keys;
  }

  function bundleNameFor(bundleId) {
    var line = document.querySelector(LINE + '[data-bundle-id="' + cssEscape(bundleId) + '"]');
    return (line && line.dataset.bundleName) || 'Bundle';
  }

  function savingsFor(bundleId) {
    var head = document.querySelector(
      '.bundle-cart-group__head[data-bundle-id="' + cssEscape(bundleId) + '"]'
    );
    return (head && head.dataset.bundleSavings) || '';
  }

  /** CSS.escape is unavailable in older Safari; ids are [a-z0-9] so this suffices. */
  function cssEscape(value) {
    if (window.CSS && typeof CSS.escape === 'function') return CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function confirmMessage(name, savings) {
    var s = strings();

    if (savings && s.confirmBodySaving) {
      return s.confirmBodySaving.replace('[NAME]', name).replace('[AMOUNT]', savings);
    }

    if (s.confirmBody) return s.confirmBody.replace('[NAME]', name);

    return 'Removing this item removes the whole ' + name + ' from your cart.';
  }

  /**
   * Zeroes every line of the group in one /cart/update.js call, then lets the
   * theme's own refresh redraw both the cart page and the drawer.
   */
  function removeBundle(bundleId) {
    var keys = lineKeys(bundleId);
    if (!keys.length) return;

    var updates = {};
    keys.forEach(function (key) {
      updates[key] = 0;
    });

    var url = (window.theme && theme.routes && theme.routes.cartUpdate) || '/cart/update.js';

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart update failed');
        return res.json();
      })
      .then(function () {
        document.dispatchEvent(
          new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
        );
      })
      .catch(function () {
        var message = strings().removeError;
        if (message && window.theme && typeof theme.showQuickPopup === 'function') {
          theme.showQuickPopup(message, document.body);
        } else {
          window.location.reload();
        }
      });
  }

  /* ------------------------------------------------------------------ *
   * Confirmation dialog
   * ------------------------------------------------------------------ */

  var dialog = null;
  var pendingBundleId = null;

  function ensureDialog() {
    if (dialog) return dialog;
    if (typeof window.HTMLDialogElement === 'undefined') return null;

    var s = strings();

    dialog = document.createElement('dialog');
    dialog.className = 'bundle-confirm';
    dialog.innerHTML =
      '<h3 class="bundle-confirm__title" data-title></h3>' +
      '<p class="bundle-confirm__body" data-body></p>' +
      '<div class="bundle-confirm__actions">' +
      '<button type="button" class="btn btn--secondary" data-cancel></button>' +
      '<button type="button" class="btn btn--primary" data-confirm></button>' +
      '</div>';

    dialog.querySelector('[data-cancel]').textContent = s.confirmCancel || 'Keep bundle';
    dialog.querySelector('[data-confirm]').textContent = s.confirmRemove || 'Remove bundle';

    dialog.querySelector('[data-cancel]').addEventListener('click', function () {
      pendingBundleId = null;
      dialog.close();
    });

    dialog.querySelector('[data-confirm]').addEventListener('click', function () {
      var id = pendingBundleId;
      pendingBundleId = null;
      dialog.close();
      if (id) removeBundle(id);
    });

    document.body.appendChild(dialog);
    return dialog;
  }

  function askThenRemove(bundleId) {
    var name = bundleNameFor(bundleId);
    var message = confirmMessage(name, savingsFor(bundleId));
    var el = ensureDialog();

    // No <dialog> support: a native confirm is ugly but never leaves the
    // customer stuck with an unremovable line.
    if (!el || typeof el.showModal !== 'function') {
      if (window.confirm(message)) removeBundle(bundleId);
      return;
    }

    el.querySelector('[data-title]').textContent = strings().confirmTitle || 'Remove the whole bundle?';
    el.querySelector('[data-body]').textContent = message;
    pendingBundleId = bundleId;
    el.showModal();
  }

  /* ------------------------------------------------------------------ *
   * Interception
   * ------------------------------------------------------------------ */

  /**
   * Capture phase, on document, so this runs before CartForm's own delegated
   * click handler (assets/main.js) turns the click into a quantity update.
   */
  function onClick(evt) {
    var target = evt.target;
    if (!target || !target.closest) return;

    var groupRemove = target.closest('[data-bundle-remove]');
    if (groupRemove) {
      evt.preventDefault();
      evt.stopPropagation();
      askThenRemove(groupRemove.dataset.bundleId);
      return;
    }

    var line = target.closest(LINE);
    if (!line) return;

    var isRemove = target.closest('.cart-item__remove');
    var isLastDecrement = target.closest('.quantity-down') && currentQuantity(line) <= 1;
    if (!isRemove && !isLastDecrement) return;

    evt.preventDefault();
    evt.stopPropagation();
    askThenRemove(line.dataset.bundleId);
  }

  /** Typing 0 into the quantity box is the third way to drop a line. */
  function onChange(evt) {
    var input = evt.target;
    if (!input || !input.classList || !input.classList.contains('cart-item__quantity-input')) return;

    var line = input.closest(LINE);
    if (!line) return;
    if (parseInt(input.value, 10) > 0) return;

    evt.preventDefault();
    evt.stopPropagation();

    // Put the value back; the dialog decides what actually happens.
    input.value = input.dataset.initialValue || 1;
    askThenRemove(line.dataset.bundleId);
  }

  function currentQuantity(line) {
    var input = line.querySelector('.cart-item__quantity-input');
    return input ? parseInt(input.value, 10) || 0 : 0;
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('change', onChange, true);

  window.theme = window.theme || {};
  theme.bundleProperties = bundleProperties;
})();
